'use client'

import Cookie from 'js-cookie'
import { addBreadcrumb } from '@sentry/nextjs'
import { captureException, getDomain } from '@sylveraio/react-utils'
import {
  ACCESS_COOKIE,
  REFRESH_COOKIE,
  REVALIDATION_COOKIE,
} from '../../UserProfile/utils/signOut/constants'

const domain = getDomain(true, true)

/**
 * Sets a cookie with specified options.
 * @param name - The name of the cookie.
 * @param value - The value to store in the cookie.
 * @param days - Number of days before the cookie expires. Defaults to 7.
 */
export function setCookie(name: string, value: string, days = 7): void {
  try {
    const options = {
      expires: days,
      domain,
      secure: process.env['NODE_ENV'] !== 'development',
      path: '/',
    }
    Cookie.set(name, value, options)
    addBreadcrumb({
      message: `Cookie set: ${name}`,
      data: { name, value, options },
    })
  } catch (e) {
    captureException(e, `setCookie - ${name}`)
  }
}

/**
 * Gets the value of a specified cookie.
 * @param name - The name of the cookie to retrieve.
 * @returns The cookie value or `null` if not found.
 */
export function getCookie(name: string): string | null {
  try {
    const value = Cookie.get(name)
    addBreadcrumb({
      message: `Cookie fetched: ${name}`,
      data: { value },
    })
    return value || null
  } catch (e) {
    captureException(e, `getCookie - ${name}`)
    return null
  }
}

/**
 * Clears cookies based on type.
 * @param cookieType - Optional type of cookie to clear.
 */
export function clearCookies(
  cookieType?: 'access' | 'refresh' | 'revalidation',
): void {
  addBreadcrumb({
    message: 'Clearing cookies',
    data: { cookieType },
  })

  const cookiesToClear = {
    access: ACCESS_COOKIE,
    refresh: REFRESH_COOKIE,
    revalidation: REVALIDATION_COOKIE,
  }

  if (!cookieType) {
    Object.values(cookiesToClear).forEach((cookie) => {
      try {
        Cookie.remove(cookie, { domain, path: '/' })
      } catch (e) {
        captureException(e, `clearCookies - ${cookie}`)
      }
    })
  } else {
    const cookieName = cookiesToClear[cookieType]
    try {
      Cookie.remove(cookieName, { domain, path: '/' })
    } catch (e) {
      captureException(e, `clearCookies - ${cookieName}`)
    }
  }
}
